import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import { Input } from 'src/components/ui/input';
import { PortalDsButton } from 'src/components/portal-design-system/portal-ds-button';
import { PortalDsField, PortalDsInput } from 'src/components/portal-design-system/portal-ds-field';
import { portalDs } from 'src/components/portal-design-system/portal-ds-classes';
import { cn } from 'src/lib/utils';
import { ChevronDown } from 'lucide-react';
import { Company } from 'src/pages/billing/types/billing.types';
import { Client } from '../../types/client.types';
import { isValidClientWebsite, normalizeClientWebsite } from '../../utils/client-url.utils';
import { useCreateClientMutation } from '../../endpoints/client.endpoints';
import { useUploadImageMutation } from 'src/redux/survey/survey.endpoints';
import { useDispatch } from 'react-redux';
import notificationService from 'src/services/notification/notification.service';

const LOGO_ACCEPT = 'image/png,image/jpeg,image/jpg,image/webp';
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
const MAX_LOGO_BYTES = 500 * 1024;

const WEBSITE_HELPER =
  'Adding a website lets Studio automatically generate brand assets such as logos and colours.';

interface CreateClientModalProps {
  open: boolean;
  companies: Company[];
  onOpenChange: (open: boolean) => void;
  onSuccess: (client: Client) => void;
}

export const CreateClientModal: React.FC<CreateClientModalProps> = ({
  open,
  companies,
  onOpenChange,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const [createClient, { isLoading: isCreating }] = useCreateClientMutation();
  const [uploadImage] = useUploadImageMutation();

  const [title, setTitle] = useState('');
  const [website, setWebsite] = useState('');
  const [websiteError, setWebsiteError] = useState<string | null>(null);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [companyMenuSearch, setCompanyMenuSearch] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [showNoWebsiteConfirm, setShowNoWebsiteConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dialogContent, setDialogContent] = useState<HTMLDivElement | null>(null);

  const resetForm = () => {
    setTitle('');
    setWebsite('');
    setWebsiteError(null);
    setTitleError(null);
    setSelectedCompanyId(null);
    setCompanyMenuSearch('');
    setLogoFile(null);
    setLogoPreview(null);
    setShowNoWebsiteConfirm(false);
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const companyOptions = useMemo(
    () =>
      companies.map((company) => ({
        value: company.id,
        label: company.company_name || company.id,
      })),
    [companies]
  );

  const filteredCompanyOptions = useMemo(() => {
    const query = companyMenuSearch.trim().toLowerCase();
    if (!query) {
      return companyOptions;
    }
    return companyOptions.filter(
      (opt) =>
        opt.label.toLowerCase().includes(query) ||
        opt.value.toLowerCase().includes(query)
    );
  }, [companyMenuSearch, companyOptions]);

  const selectedCompanyLabel = useMemo(() => {
    if (!selectedCompanyId) {
      return null;
    }
    return companyOptions.find((opt) => opt.value === selectedCompanyId)?.label ?? null;
  }, [companyOptions, selectedCompanyId]);

  const handleCompanySelect = (companyId: string) => {
    setSelectedCompanyId(companyId);
    const company = companies.find((c) => c.id === companyId);
    if (company?.company_name && !title.trim()) {
      setTitle(company.company_name);
    }
  };

  const handleWebsiteBlur = () => {
    if (!website.trim()) {
      setWebsiteError(null);
      return;
    }
    const normalized = normalizeClientWebsite(website);
    setWebsite(normalized);
    setWebsiteError(isValidClientWebsite(normalized) ? null : 'Enter a valid URL');
  };

  const handleLogoChange = (file: File | null) => {
    if (!file) {
      setLogoFile(null);
      setLogoPreview(null);
      return;
    }
    if (!LOGO_TYPES.includes(file.type)) {
      notificationService.handleError(null, dispatch, 'Logo must be PNG, JPG, or WebP');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      notificationService.handleError(null, dispatch, 'Logo must be under 500KB');
      return;
    }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const uploadLogo = async(client: Client) => {
    if (!logoFile) {
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', logoFile);
      formData.append('type', 'client_logo');
      formData.append('uuid', client.ext_client_id);
      await uploadImage({ formData, uuid: client.ext_client_id }).unwrap();
    } catch (error) {
      console.error('Logo upload failed after client create:', error);
      notificationService.handleError(null, dispatch, 'Client created but logo upload failed');
    }
  };

  const submitCreate = async() => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setTitleError('Client name is required');
      return;
    }

    const normalizedWebsite = website.trim() ? normalizeClientWebsite(website) : null;
    if (normalizedWebsite && !isValidClientWebsite(normalizedWebsite)) {
      setWebsiteError('Enter a valid URL');
      return;
    }

    try {
      const payload: { title: string; website?: string | null; company_ids?: string[] } = {
        title: trimmedTitle,
      };
      if (normalizedWebsite) {
        payload.website = normalizedWebsite;
      }
      if (selectedCompanyId) {
        payload.company_ids = [selectedCompanyId];
      }

      const client = await createClient(payload).unwrap();
      await uploadLogo(client);
      onOpenChange(false);
      onSuccess(client);
    } catch (error) {
      console.error('Failed to create client:', error);
      notificationService.handleError(null, dispatch, 'Failed to create client');
    }
  };

  const handleSubmit = () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setTitleError('Client name is required');
      return;
    }
    setTitleError(null);

    if (website.trim()) {
      const normalized = normalizeClientWebsite(website);
      if (!isValidClientWebsite(normalized)) {
        setWebsiteError('Enter a valid URL');
        return;
      }
      setWebsiteError(null);
      void submitCreate();
      return;
    }

    setShowNoWebsiteConfirm(true);
  };

  const isSubmitting = isCreating;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        ref={setDialogContent}
        className="bg-white sm:max-w-lg"
        onPointerDownOutside={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className={portalDs.sectionTitle}>
            {showNoWebsiteConfirm ? 'Create without a website?' : 'New client'}
          </DialogTitle>
        </DialogHeader>

        {showNoWebsiteConfirm ? (
          <p className={portalDs.sheetDescription}>
            Brand assets won&apos;t be generated automatically without a website. You can add one later from the client drawer.
          </p>
        ) : (
          <div className="flex flex-col gap-4 py-1">
            <PortalDsField
              label="Company"
              htmlFor="create-client-company"
            >
              <DropdownMenu
                modal={false}
                onOpenChange={(isOpen) => {
                  if (isOpen) {
                    setCompanyMenuSearch('');
                  }
                }}
              >
                <DropdownMenuTrigger asChild>
                  <PortalDsButton
                    id="create-client-company"
                    variant="secondary"
                    size="sm"
                    className="w-full justify-between font-normal text-left"
                    type="button"
                  >
                    <span className="truncate">
                      {selectedCompanyLabel || 'Link to billing company (optional)'}
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0 ml-1 opacity-50" />
                  </PortalDsButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  container={dialogContent}
                  className="min-w-[var(--radix-dropdown-menu-trigger-width)] max-h-[320px] flex flex-col overflow-hidden p-0"
                  onCloseAutoFocus={(e) => e.preventDefault()}
                >
                  <div className="shrink-0 border-b border-[#EDEEF2] bg-popover p-2">
                    <Input
                      type="search"
                      placeholder="Search companies..."
                      value={companyMenuSearch}
                      onChange={(e) => setCompanyMenuSearch(e.target.value)}
                      className={cn(portalDs.input, 'h-8 text-sm')}
                      autoComplete="off"
                      onClick={(e) => e.stopPropagation()}
                      onPointerDown={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="max-h-[240px] overflow-y-auto p-1">
                    {filteredCompanyOptions.length === 0 ? (
                      <div className={cn(portalDs.hint, 'px-2 py-3')}>
                        No matching companies
                      </div>
                    ) : (
                      filteredCompanyOptions.map((opt) => (
                        <DropdownMenuItem
                          key={opt.value}
                          onSelect={() => handleCompanySelect(opt.value)}
                        >
                          {opt.label}
                        </DropdownMenuItem>
                      ))
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </PortalDsField>

            <PortalDsField
              label="Client name"
              htmlFor="create-client-title"
              required
              error={titleError ?? undefined}
            >
              <PortalDsInput
                id="create-client-title"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (titleError) {
                    setTitleError(null);
                  }
                }}
                placeholder="Client name"
              />
            </PortalDsField>

            <PortalDsField
              label="Website"
              htmlFor="create-client-website"
              hint={WEBSITE_HELPER}
              error={websiteError ?? undefined}
            >
              <PortalDsInput
                id="create-client-website"
                value={website}
                onChange={(e) => {
                  setWebsite(e.target.value);
                  if (websiteError) {
                    setWebsiteError(null);
                  }
                }}
                onBlur={handleWebsiteBlur}
                placeholder="https://example.com"
              />
            </PortalDsField>

            <PortalDsField
              label="Logo"
              htmlFor="create-client-logo"
            >
              <div className="flex items-center gap-3">
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="h-14 w-14 rounded-lg border border-[#EDEEF2] object-contain"
                  />
                ) : (
                  <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-dashed border-[#D9DBE3] bg-[#F7F8FA] text-xs text-[#9CA3AF]">
                    No logo
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  <PortalDsButton
                    variant="secondary"
                    size="sm"
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Choose file
                  </PortalDsButton>
                  <p className={portalDs.hint}>
                    PNG, JPG, or WebP up to 500KB
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  id="create-client-logo"
                  type="file"
                  accept={LOGO_ACCEPT}
                  className="hidden"
                  onChange={(e) => handleLogoChange(e.target.files?.[0] ?? null)}
                />
              </div>
            </PortalDsField>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {showNoWebsiteConfirm ? (
            <>
              <PortalDsButton
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => setShowNoWebsiteConfirm(false)}
                disabled={isSubmitting}
              >
                Go back
              </PortalDsButton>
              <PortalDsButton
                variant="primary"
                size="sm"
                type="button"
                onClick={() => void submitCreate()}
                disabled={isSubmitting}
              >
                Create client
              </PortalDsButton>
            </>
          ) : (
            <>
              <PortalDsButton
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </PortalDsButton>
              <PortalDsButton
                variant="primary"
                size="sm"
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                Create client
              </PortalDsButton>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateClientModal;
